// src/pages/FrogsPage.js
import React, { useEffect, useState } from 'react';
import { getProducts } from '../services/api';
import ProductCard from '../components/Products/ProductCard';
import SpeciesLearnGrid from '../components/Products/SpeciesLearnGrid';
import EmptyStateWayOn from '../components/common/EmptyStateWayOn';
import { Row, Col, Container } from 'react-bootstrap';
import SEO from '../components/SEO';
import { FULL_KEYWORDS, ORGANIZATION_JSONLD, WEBSITE_JSONLD } from '../constants/seoKeywords';
import SkeletonCardGrid from '../components/common/SkeletonCardGrid';
import '../styles/products.css';
import '../styles/ff-empty-state.css';
import '../styles/skeletons.css';

const FrogsPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  // Distinguish "loaded, empty list" from "load failed" so a slow /
  // intermittent backend doesn't display "No frogs available" to a
  // customer who's just having a bad-network moment (2026-07-02 —
  // observed intermittent 30s+ timeouts on the products endpoint;
  // silent try/catch was making sold-out and network-error look
  // identical in the storefront).
  const [loadError, setLoadError] = useState(null);

  const fetchAvailableFrogs = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      // featuredFirst: server-side merch ordering (gate-2, operator
      // delegated 2026-08-24) — marketing-featured animals first, ties
      // broken by newest listed arrival. Same ordering the homepage
      // "Currently Available" rail uses, so the two surfaces agree.
      const response = await getProducts({ speciesType: 0, isForSale: true, featuredFirst: true });
      setProducts(response);
    } catch (error) {
      // Surface both to console and the visible UI. If the customer
      // clicks retry, we re-attempt cleanly.
      console.error('[FrogsPage] load failed:', error?.message || error);
      setLoadError(
        error?.code === 'ECONNABORTED' || /timeout/i.test(error?.message || '')
          ? 'Having trouble reaching our inventory just now. Please try again in a moment.'
          : 'Something went wrong loading available frogs. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAvailableFrogs();
  }, []);

  return (
    <>
      <SEO
        title="Large Obligate Dart Frogs for Sale — Oophaga & More"
        description="Shop large obligate dart frogs — Oophaga sylvatica & histrionica — plus captive-bred poison dart frogs. Healthy animals, expert care guides, live-arrival guarantee."
        canonical="https://feelingfroggy.llc/frogs"
        keywords={FULL_KEYWORDS}
        jsonLd={[ORGANIZATION_JSONLD, WEBSITE_JSONLD]}
      />
      <Container className="products-page">
        <h1 className="ff-heading-h1">Available Now – Frogs</h1>

        {loading ? (
          <SkeletonCardGrid count={8} />
        ) : loadError ? (
          <div className="text-center py-5">
            <p className="text-muted mb-3">{loadError}</p>
            <button
              type="button"
              className="ff-btn ff-btn--secondary"
              onClick={fetchAvailableFrogs}
            >
              Try again
            </button>
          </div>
        ) : products.length === 0 ? (
          /* #103 zero-inventory graceful degradation — LEARN mode: zero
             products swaps the empty grid for species cards (care guide
             link + per-species arrivals notify). Full/lean weeks never
             reach this branch, so they are byte-identical to before.
             Species fetch failure falls back to the old empty state. */
          <SpeciesLearnGrid
            speciesType={0}
            fallback={(
              <div className="empty-state">
                <h3>No frogs currently available</h3>
                <p>New arrivals are added regularly — check back soon!</p>
                <EmptyStateWayOn showBrowseFrogs={false} />
              </div>
            )}
          />
        ) : (
          <Row className="g-4">
            {products.map((product, index) => (
              <Col key={product.id} xs={12} sm={6} md={4}>
                {/* #99: first two desktop rows load their photos eagerly —
                    the primary card image riding loading="lazy" was the
                    only path where a visible card could sit with its image
                    unrequested (intermittent operator reports). */}
                <ProductCard product={product} eagerImage={index < 6} />
              </Col>
            ))}
          </Row>
        )}
      </Container>
    </>
  );
};

export default FrogsPage;